import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { CustomerLayout } from "@/components/layout/CustomerLayout";
import { PublicLayout } from "@/components/layout/PublicLayout"; // 🛠️ ADDED: Public Layout
import { useAuthStore } from "@/store/auth-store";

// Auth
import { LoginPage } from "@/features/auth/LoginPage";
import { RegisterPage } from "@/features/auth/RegisterPage";

// Customer
import { DashboardPage } from "@/features/customer/DashboardPage";
import { CatalogPage } from "@/features/customer/CatalogPage";
import { ProductDetailPage } from "@/features/customer/ProductDetailPage";
import { OrdersPage } from "@/features/customer/OrdersPage";
import { OrderDetailPage } from "@/features/customer/OrderDetailPage";
import { PaymentSubmitPage } from "@/features/customer/PaymentSubmitPage";
import { QualificationPage } from "@/features/customer/QualificationPage";
import { MembershipPage } from "@/features/customer/MembershipPage";
import { EntriesPage } from "@/features/customer/EntriesPage";
import { EntryDetailPage } from "@/features/customer/EntryDetailPage";
import { WalletPage } from "@/features/customer/WalletPage";
import { WithdrawalRequestPage } from "@/features/customer/WithdrawalRequestPage";
import { NotificationsPage } from "@/features/customer/NotificationsPage";
import { ProfilePage } from "@/features/customer/ProfilePage";
import { CheckoutPage } from "@/features/customer/CheckoutPage";
import { VendorShopPage } from "@/features/public/VendorShopPage";
import { CategoriesPage } from "@/features/customer/CategoriesPage";

// Customer Extensions
import { ReferralPage } from "@/features/customer/ReferralPage";
import { StarMemberPage } from "@/features/customer/StarMemberPage";
import { PackagePaymentPage } from "@/features/customer/PackagePaymentPage";

// Agent Portal
import { AgentLayout } from "@/features/agent/AgentLayout";
import { AgentDashboardPage } from "@/features/agent/AgentDashboardPage";
import { AgentDistributePage } from "@/features/agent/AgentDistributePage";
import { AgentHistoryPage } from "@/features/agent/AgentHistoryPage";
import { AgentVendorOrdersPage } from "@/features/agent/AgentVendorOrdersPage";

// Admin
import { AdminLayout } from "@/features/admin/AdminLayout";
import { AdminDashboardPage } from "@/features/admin/AdminDashboardPage";
import { ConfigurationPage } from "@/features/admin/ConfigurationPage";
import { UsersPage } from "@/features/admin/UsersPage";
import { UserDetailPage } from "@/features/admin/UserDetailPage";
import { CatalogAdminPage } from "@/features/admin/CatalogAdminPage";
import { InventoryPage } from "@/features/admin/InventoryPage";
import { OrdersAdminPage } from "@/features/admin/OrdersAdminPage";
import { OrderDetailAdminPage } from "@/features/admin/OrderDetailAdminPage";
import { PaymentsQueuePage } from "@/features/admin/PaymentsQueuePage";
import { PaymentGatewaysPage } from "@/features/admin/PaymentGatewaysPage";
import { MembershipAdminPage } from "@/features/admin/MembershipAdminPage";
import { RewardRulesPage } from "@/features/admin/RewardRulesPage";
import { PlacementQueuePage } from "@/features/admin/PlacementQueuePage";
import { TreeExplorerPage } from "@/features/admin/TreeExplorerPage";
import { NotificationsAdminPage } from "@/features/admin/NotificationsAdminPage";
import { OutboxPage } from "@/features/admin/OutboxPage";
import { AuditLogPage } from "@/features/admin/AuditLogPage";
import { SystemHealthPage } from "@/features/admin/SystemHealthPage";
import { WithdrawalsAdminPage } from "@/features/admin/WithdrawalsAdminPage";
import { WithdrawalSettingsPage } from "@/features/admin/WithdrawalSettingsPage"; // 🛠️ Import at the top

// Admin Extensions
import { StorefrontAdminPage } from "@/features/admin/StorefrontAdminPage";
import { StorefrontPage } from "@/features/public/StorefrontPage";
import { ReferralConfigPage } from "@/features/admin/referral/ReferralConfigPage";
import { ReferralCommissionsPage } from "@/features/admin/referral/ReferralCommissionsPage";
import { StarConfigPage } from "@/features/admin/star/StarConfigPage";
import { StarOffersPage } from "@/features/admin/star/StarOffersPage";
import { StarOfferDetailPage } from "@/features/admin/star/StarOfferDetailPage";
import { AgentsListPage } from "@/features/admin/agents/AgentsListPage";
import { AgentDetailPage } from "@/features/admin/agents/AgentDetailPage";
import { AgentToAgentTransferPage } from "@/features/agent/AgentToAgentTransferPage";

import { ReportsHubPage } from "@/features/admin/reports/ReportsHubPage";
import { SalesReportPage } from "@/features/admin/reports/SalesReportPage";
import { TopUsersReportPage } from "@/features/admin/reports/TopUsersReportPage";
import { UserPerformancePage } from "@/features/admin/reports/UserPerformancePage";
import { SpecialOffersHubPage } from "@/features/admin/special-offers/SpecialOffersHubPage";
import { SpecialOfferDetailPage } from "@/features/admin/special-offers/SpecialOfferDetailPage";
import { TopUsersPickerPage } from "@/features/admin/special-offers/TopUsersPickerPage";

function RequireAuth({ children }: { children: JSX.Element }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function RedirectIfAuth({ children }: { children: JSX.Element }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

export const router = createBrowserRouter([

  // 1. 🌟 PUBLIC ROUTES (No login required - wrapped in PublicLayout)
  { 
    path: "/", 
    element: <PublicLayout />, 
    children: [
      { index: true, element: <StorefrontPage /> },
      { path: "catalog", element: <CatalogPage /> },
      { path: "catalog/:productCode", element: <ProductDetailPage /> },
      { path: "vendor/:vendorId", element: <VendorShopPage /> },
      { path: "/categories", element: <CategoriesPage /> },
    ]
  },

  // 2. AUTH ROUTES
  {
    element: <RedirectIfAuth><AuthLayout /></RedirectIfAuth>,
    children: [
      { path: "/login",    element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
    ],
  },

  // 3. CUSTOMER PORTAL
  {
    path: "/dashboard",
    element: <RequireAuth><CustomerLayout /></RequireAuth>,
    children: [
      { index: true,                  element: <DashboardPage /> },
      // 🛠️ Catalog and Product Details are removed from here (they are now public)
      { path: "orders",               element: <OrdersPage /> },
      { path: "orders/:orderId",      element: <OrderDetailPage /> },
      { path: "checkout",             element: <CheckoutPage /> },
      { path: "payments",             element: <OrdersPage /> }, // Note: You might want to remove this if payments list is same as orders
      { path: "qualification",        element: <QualificationPage /> },
      { path: "membership",           element: <MembershipPage /> },
      { path: "entries",              element: <EntriesPage /> },
      { path: "entries/:entryId",     element: <EntryDetailPage /> },
      { path: "wallet",               element: <WalletPage /> },
      { path: "wallet/withdraw",      element: <WithdrawalRequestPage /> },
      { path: "notifications",        element: <NotificationsPage /> },
      { path: "profile",              element: <ProfilePage /> },
      { path: "referrals",            element: <ReferralPage /> },
      { path: "star",                 element: <StarMemberPage /> },
      { path: "/dashboard/membership/pay/:purchaseId", element: <PackagePaymentPage /> },

    ],
  },

  // 4. AGENT PORTAL
  {
    path: "/agent",
    element: <RequireAuth><AgentLayout /></RequireAuth>,
    children: [
      { index: true,         element: <AgentDashboardPage /> },
      { path: "distribute",  element: <AgentDistributePage /> },
      { path: "history",     element: <AgentHistoryPage /> },
      { path: "orders",      element: <AgentVendorOrdersPage /> },
      { path: "transfer", element: <AgentToAgentTransferPage /> },
    ],
  },

  // 5. ADMIN CONSOLE
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      { index: true,                element: <AdminDashboardPage /> },
      { path: "health",             element: <SystemHealthPage /> },
      { path: "configuration",      element: <ConfigurationPage /> },
      { path: "users",              element: <UsersPage /> },
      { path: "users/:userId",      element: <UserDetailPage /> },
      { path: "catalog",            element: <CatalogAdminPage /> },
      { path: "inventory",          element: <InventoryPage /> },
      { path: "orders",             element: <OrdersAdminPage /> },
      { path: "orders/:orderId",    element: <OrderDetailAdminPage /> },
      { path: "payments",           element: <PaymentsQueuePage /> },
      { path: "payment-gateways",   element: <PaymentGatewaysPage /> },
      { path: "membership",         element: <MembershipAdminPage /> },
      { path: "reward-rules",       element: <RewardRulesPage /> },
      { path: "placement",          element: <PlacementQueuePage /> },
      { path: "tree",               element: <TreeExplorerPage /> },
      { path: "notifications",      element: <NotificationsAdminPage /> },
      { path: "outbox",             element: <OutboxPage /> },
      { path: "audit",              element: <AuditLogPage /> },
      { path: "withdrawals",        element: <WithdrawalsAdminPage /> },
      { path: "withdrawals/settings", element: <WithdrawalSettingsPage /> }, // 🛠️ NEW ROUTE
      { path: "storefront",         element: <StorefrontAdminPage /> }, 
      { path: "referrals/config",       element: <ReferralConfigPage /> },
      { path: "referrals/commissions",  element: <ReferralCommissionsPage /> },
      { path: "star/config",            element: <StarConfigPage /> },
      { path: "star/offers",            element: <StarOffersPage /> },
      { path: "star/offers/:offerId",   element: <StarOfferDetailPage /> },
      { path: "agents",                 element: <AgentsListPage /> },
      { path: "agents/:agentId",        element: <AgentDetailPage /> },
      { path: "reports",                element: <ReportsHubPage /> },
      { path: "reports/sales",          element: <SalesReportPage /> },
      { path: "reports/top-users",      element: <TopUsersReportPage /> },
      { path: "reports/user-perf",      element: <UserPerformancePage /> },
      { path: "special-offers",         element: <SpecialOffersHubPage /> },
      { path: "special-offers/top-users", element: <TopUsersPickerPage /> },
      { path: "special-offers/:pk",     element: <SpecialOfferDetailPage /> },
    ],
  },
  
  { path: "*", element: <Navigate to="/" replace /> },
]);